// src/components/AddGroupPage.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faLayerGroup,
    faPlus,
    faCheck,
    faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';

import './FormPage.css';

const addGroupCallable = httpsCallable(functions, 'addGroup');

function AddGroupPage() {
    const [groupName, setGroupName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const validateForm = () => {
        setError(null);
        if (!groupName.trim()) {
            setError("Group Name cannot be empty.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm() || !currentUser) {
            if (!currentUser) setError("You must be logged in to add a group.");
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        const payload = {
            groupName: groupName.trim(),
        };

        try {
            const result = await addGroupCallable(payload);
            const resultData = result.data;

            if (resultData?.success) {
                setSuccessMessage(resultData.message || "Group created successfully!");
                setGroupName('');
                setTimeout(() => {
                    navigate('/groups', { state: { message: `Group '${payload.groupName}' created.` } });
                }, 1500);
            } else {
                setError(resultData?.message || "Failed to create group.");
            }
        } catch (err) {
            console.error("Error calling 'addGroup' function:", err);
            let specificError = "An unexpected error occurred.";
            if (err.code && err.message) {
                if (err.code === 'permission-denied') { specificError = "You do not have permission to create groups."; }
                else if (err.code === 'already-exists') { specificError = "A group with this name already exists in your organization."; }
                else if (err.code === 'invalid-argument') { specificError = "Invalid data provided (e.g., group name empty)."; }
                else { specificError = `Server error: ${err.message}`; }
            }
            setError(specificError);
        } finally {
            if (!successMessage) {
                setIsLoading(false);
            }
        }
    };

    const formInputsDisabled = isLoading || !!successMessage;

    return (
        <div className="form-page">
            {/* Page Header */}
            <div className="form-page-header">
                <div className="container">
                    <div className="form-page-header-content">
                        <div className="form-page-header-left">
                            <Link to="/groups" className="form-back-btn" title="Back to Groups">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="form-page-title">Create Group</h1>
                                <p className="form-page-subtitle">Add a new group to your organization</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Form Card */}
                <div className="form-card">
                    <div className="form-card-body">
                        {/* Error Alert */}
                        {error && (
                            <div className="form-alert form-alert-danger">
                                <FontAwesomeIcon icon={faExclamationCircle} className="form-alert-icon" />
                                <div className="form-alert-content">{error}</div>
                            </div>
                        )}

                        {/* Success Alert */}
                        {successMessage && (
                            <div className="form-alert form-alert-success">
                                <FontAwesomeIcon icon={faCheck} className="form-alert-icon" />
                                <div className="form-alert-content">{successMessage}</div>
                            </div>
                        )}

                        {!successMessage && (
                            <form onSubmit={handleSubmit} noValidate>
                                {/* Group Info Section */}
                                <div className="form-section">
                                    <h3 className="form-section-title">
                                        <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: '0.5rem' }} />
                                        Group Information
                                    </h3>

                                    <div className="form-field">
                                        <label className="form-field-label">
                                            Group Name <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-field-input"
                                            placeholder="Enter group name (e.g., Youth Group, Choir)"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            disabled={formInputsDisabled}
                                            maxLength={100}
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="form-actions">
                                    <button
                                        type="submit"
                                        className="form-submit-btn form-submit-btn-success"
                                        disabled={formInputsDisabled}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                Creating Group...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faPlus} />
                                                Create Group
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                        {successMessage && (
                            <div className="form-actions" style={{ borderTop: 'none', paddingTop: 0 }}>
                                <button
                                    type="button"
                                    className="form-submit-btn"
                                    onClick={() => navigate('/groups')}
                                >
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                    Go to Groups
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddGroupPage;
